import { BaseSyntheticEvent, RefObject } from 'react'

import Noty from 'noty'

export const keyBy = <T extends object>(
  array: readonly T[],
  setter: keyof T | ((value: T, index: number, array: readonly T[]) => string | number),
): Record<string, T> =>
  (array || []).reduce(
    (obj, value, ...args) => (
      (obj[(typeof setter === 'function' ? setter(value, ...args) : value[setter]) as string] = value),
      obj
    ),
    {} as Record<string, T>,
  )

export const pickBy = (object: Record<string, string>): Record<string, string> => {
  const obj = {} as Record<string, string>
  for (const key in object) {
    if (object[key]) {
      obj[key] = object[key]
    }
  }
  return obj
}

export const filterMap = <T, U>(
  array: readonly T[],
  callbackfn: (currentValue: T, currentIndex: number, array: readonly T[]) => U | undefined,
): U[] => {
  return array.reduce((previousValue, ...rest) => {
    const value = callbackfn(...rest)
    if (value) previousValue.push(value)
    return previousValue
  }, [] as U[])
}

export const groupBy = <T extends object>(
  array: T[],
  setter: keyof T | ((value: T, index: number, array: T[]) => string | number),
): { [key: string]: T[] } =>
  (array || []).reduce(
    (ary, value, ...args) => {
      const key = (typeof setter === 'function' ? setter(value, ...args) : value[setter]) as string
      ;(ary[key] ||= []).push(value)
      return ary
    },
    {} as Record<string, T[]>,
  )

export const isBoolean = (value?: unknown): value is boolean => typeof value === 'boolean'
export const humanize = (str: string): string =>
  str
    .replace(/^[\s_]+|[\s_]+$/g, '')
    .replace(/[_\s]+/g, ' ')
    .replace(/^[a-z]/, (m) => m.toUpperCase())

export const noty = (message: string, type: Noty.Type = 'error', timeout = 5000): void => {
  new Noty({
    text: `<div class="text-center">${message || 'Something went wrong'}</div>`,
    type,
    layout: 'bottomCenter',
    theme: 'sunset',
    timeout: timeout || 3000,
  }).show()
}

export const stopPropagation = (e: BaseSyntheticEvent): void => e.stopPropagation()

export const scrollToTopOfElement = (elementRef: RefObject<HTMLFormElement | HTMLDivElement>, offset = 2): void => {
  setTimeout(() => {
    elementRef.current?.scrollIntoView()

    const headerHight = (document.getElementsByClassName('js-navbar')[0] as HTMLScriptElement).offsetHeight
    const fullHeaderHight = headerHight * offset
    const scrolledY = window.scrollY
    const elementTop = elementRef.current?.getBoundingClientRect().top
    if (elementTop && elementTop < fullHeaderHight && scrolledY) {
      const headerHight = (document.getElementsByClassName('js-navbar')[0] as HTMLScriptElement).offsetHeight
      window.scroll(0, scrolledY - headerHight * offset)
    }
  }, 100)
}

// TODO: fix for refreshing ORDER TOTAL in Safari. find a right way to do it
export const refreshingOrderTotalInSafari = (): void => {
  const orderBlock = document.getElementsByClassName('checkout-order')[0] as HTMLElement
  orderBlock.style.display = 'none'
  setTimeout(() => (orderBlock.style.display = 'block'), 100)
}

export const convertFormattedToFloat = (amount: string | number): number =>
  typeof amount === 'string' ? parseFloat(amount.replace(/[^\d.-]+/g, '')) : amount

export function mergeRefs<T = unknown>(
  ...refs: (React.MutableRefObject<T> | React.LegacyRef<T> | null | undefined)[]
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value)
      } else if (ref != null) {
        ;(ref as React.MutableRefObject<T | null>).current = value
      }
    })
  }
}
